import React, { useEffect } from "react";
import { useRecoilValue } from "recoil";
import { ColumnSortOption } from "../../../../../Component/Model/Dto/ColumnSortOption";
import { EEMonitoringProgress } from "../../../../../Component/Model/Dto/EmploymentEquity/Plan/EEMonitoringProgress";
import { EntriesPerPage } from "../../../../../Component/Model/Table/Table";
import { defaultObject } from "../../../../../Helpers/typescriptHelper";
import { ScorecardAtom } from "../../../../../State/Scorecard.Atom";
import { toast } from "react-toastify";
import { MonitoringProgressTable, MonitoringProgressTableMapper } from "./MonitoringProgressTableColumn";
import { DataTable } from "../../../../../Component/Common/Components/DataTable/DataTable";
import { ConfirmationDialog } from "../../../../../Component/Common/Components/ConfirmationDialog";
import { MonitoringProgressModal } from "./MonitoringProgressModal";
import { MonitoringProgressService } from "../../../../../Component/Api/EmploymentEquity/Plan/MonitoringProgress/MonitoringProgressService";
import { ConsolidationAtom } from "../../../../../State/Consolidation.Atom";
import { UserActivityService } from "../../../../../Component/Api/UserActivity/UserActivityService";
import { MonitoringProgressSection } from "../../../../../Component/Common/Constants/UserActivityTrackingSections";
import { UserActivityActionType } from "../../../../../Component/Model/Enum/BEE123Enums";

export const MonitoringProgress = () => {
    const [previewPopup, setPreviewPopup] = React.useState<boolean>(false);
    const [showConfirmationDialog, setShowConfirmationDialog] = React.useState<boolean>(false);
    const [monitoringProgressData, setMonitoringProgressData] = React.useState<EEMonitoringProgress.DtoIn[]>([]);
    const [entriesPerPage, setEntriesPerPage] = React.useState<EntriesPerPage>({ defaultValue: 10 });
    const [pageNumber, setPageNumber] = React.useState<number>(1);
    const [filteredItemsCount, setFilteredItemsCount] = React.useState<number>(0);
    const [totalItemsCount, setTotalItemsCount] = React.useState<number>(0);
    const [isLoading, setIsLoading] = React.useState<boolean>(false);
    const [selectedMonitoringProgressId, setSelectedMonitoringProgressId] = React.useState<string>("");
    const [columnSortOptions, setColumnSortOptions] = React.useState<ColumnSortOption[]>([]);
    const scorecard = useRecoilValue(ScorecardAtom);
    const isConsolidatedScorecard = useRecoilValue(ConsolidationAtom);

    const link = 'EmploymentEquityMonitoringProgress';
    const scorecardId = scorecard.scorecardId;
    const confirmDeleteMessage = "This action cannot be reversed. Are you sure you want to delete the procedure to monitor and evaluate the implementation of the plan?"

    const entriesPerPageChanged = (entriesPerPage: number) => {
        setEntriesPerPage({ defaultValue: entriesPerPage });
        setPageNumber(1);
    }

    const pageNumberChanged = (pageNumber: number) => {
        setPageNumber(pageNumber);
    }

    const columnSortingChanged = (sortOption: ColumnSortOption) => {
        setColumnSortOptions([sortOption]);
        setPageNumber(1);
    }

    const handleClosePopup = () => {
        setPreviewPopup(false);
        setSelectedMonitoringProgressId("");
        fetchData();
    }

    const handleEdit = (id: string) => {
        setSelectedMonitoringProgressId(id);
        setPreviewPopup(true);
    }

    const handleAdd = () => {
        setPreviewPopup(true);
    }

    const handleConfirmDelete = (id: string) => {
        setSelectedMonitoringProgressId(id);
        setShowConfirmationDialog(true);
    }

    const monitoringProgressLog = (actionType: number, actionDescription: string) => {
         UserActivityService.createActivityLog({
            section: MonitoringProgressSection,
            actionType: actionType,
            actionDescription: actionDescription,
            scorecardId: scorecardId,
            requestUrl: window.location.href
        });
    }

    const handleDelete = async () => {
        try {
            await MonitoringProgressService.DeleteMonitoringProgress(selectedMonitoringProgressId);
            monitoringProgressLog(UserActivityActionType.Delete, "Delete a progress entry");
            toast.success("Monitoring Progress deleted succesfully");
            handleCloseConfirmation();
            fetchData();

        } catch (error) {
            console.log(error)
        }
    }

    const handleCloseConfirmation = () => {
        setShowConfirmationDialog(false);
        setSelectedMonitoringProgressId("");
    }

    const bulkActions =
    [{
        label: "Add",
        action: handleAdd
    }]

    const monitoringProgressSearchRequest : EEMonitoringProgress.MonitoringProgressSearchRequest = {
        pageNumber: pageNumber as number,
        pageSize: entriesPerPage.defaultValue as number,
        scorecardId: scorecardId,
        columnSortOptions: columnSortOptions
    };

    const fetchData = async () => {
        setIsLoading(true);
        
        try {
            let searchResult = await MonitoringProgressService.SearchMonitoringProgress(monitoringProgressSearchRequest);
            if (searchResult.data){
                setMonitoringProgressData(searchResult.data.results);
                setFilteredItemsCount(searchResult.data.filteredItemsCount);
                setTotalItemsCount(searchResult.data.totalItemsCount);
            }
        } catch (error) {
            console.log(error)
        } finally {
            setIsLoading(false);
        }
    };
    
    useEffect(() => {
        fetchData();
    }, [entriesPerPage, pageNumber, columnSortOptions]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        monitoringProgressLog(UserActivityActionType.View, "View list of progress entries");
    }, [])

    const tableData = MonitoringProgressTableMapper(
        monitoringProgressData, 
        handleEdit,
        handleConfirmDelete,
        isConsolidatedScorecard
    );

    return (
        <div style={{ marginTop: "5px" }}>
            <DataTable
                key={"MonitoringProgressTable"}
                table={tableData}
                entriesPerPage={entriesPerPage}
                pageNumber={pageNumber}
                filteredItemsCount={filteredItemsCount}
                totalItemsCount={totalItemsCount}
                entriesPerPageChanged={entriesPerPageChanged}
                pageNumberChanged={pageNumberChanged}
                isLoading={isLoading}
                bulkActions={bulkActions}
                manualSorting={true}
                columnSortingChanged={columnSortingChanged}
            ></DataTable>

            {previewPopup && 
                <MonitoringProgressModal 
                    id={selectedMonitoringProgressId !== "" ? selectedMonitoringProgressId : undefined} 
                    handleCancel={handleClosePopup} 
                    refreshTable={fetchData} 
                    scorecardId={scorecardId} 
                    link={link} 
                    setMonitoringProgressId={setSelectedMonitoringProgressId} 
                    previewPopup={previewPopup} 
                />
            }

            <ConfirmationDialog
                showDialog={showConfirmationDialog}
                handleAccept={handleDelete}
                handleCancel={handleCloseConfirmation}
                heading={"Confirm Delete"}
                message={confirmDeleteMessage}
            />
        </div>
    )

}